import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { WeekCalculationService } from 'src/app/services/week-calculation.service';
import moment from 'moment';

export interface CalendarWeekChangeEvent {
  date: string;  // The selected date (Sunday of the week - start of week)
  weekDisplay: string;  // Display string like "Week 53, 2025"
}

@Component({
  selector: 'app-calendar-week-selector',
  templateUrl: './calendar-week-selector.component.html',
  styleUrls: ['./calendar-week-selector.component.css'],
  host: { '[class]': 'colClass' }
})
export class CalendarWeekSelectorComponent implements OnInit, OnChanges {
  // Input properties
  @Input() initialDate: string;
  @Input() label: string = 'Choose a date';
  @Input() colClass: string = 'col-3';

  // Output events
  @Output() selectionChange = new EventEmitter<CalendarWeekChangeEvent>();

  // Internal state
  selectedDate: string;
  CalendarWeekViewForYearData: { key: string; value: string }[] = [];

  constructor(
    private weekCalculationService: WeekCalculationService
  ) {}

  ngOnInit(): void {
    // Generate week data spanning previous year (for Week 53) and current year
    this.generateWeekData();

    // Set initial date if provided, otherwise use current week
    if (this.initialDate) {
      this.selectedDate = this.normalizeToSunday(this.initialDate);
    } else {
      // Default to current week's Sunday (start of week)
      this.selectedDate = this.normalizeToSunday(moment().format('YYYY-MM-DD'));
    }

    // Ensure selected date exists in dropdown options
    this.ensureSelectedDateInOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When initialDate changes from parent (e.g., calendar navigation), update selection
    if (changes['initialDate'] && !changes['initialDate'].firstChange) {
      const newDate = changes['initialDate'].currentValue;
      if (newDate) {
        const normalizedDate = this.normalizeToSunday(newDate);
        if (normalizedDate !== this.selectedDate) {
          this.selectedDate = normalizedDate;
          this.ensureSelectedDateInOptions();
        }
      }
    }
  }

  /**
   * Normalize any date to the Sunday (start) of its week
   * Uses Sunday-Saturday week definition matching WeekCalculationService
   */
  private normalizeToSunday(dateStr: string): string {
    const date = moment(dateStr);
    // day() returns 0 for Sunday, so we just go back to Sunday of current week
    return date.day(0).format('YYYY-MM-DD');
  }

  /**
   * Get the Sunday (start) of a specific week number in a year
   * Uses the custom week calculation logic (Sunday-Saturday weeks)
   */
  private getSundayOfWeek(year: number, weekNum: number): string {
    // Get Week 1 Sunday for the year
    const week1Start = this.getWeek1Start(year);
    // Add (weekNum - 1) weeks to get to the target week's Sunday
    const targetDate = new Date(week1Start);
    targetDate.setDate(targetDate.getDate() + (weekNum - 1) * 7);
    return moment(targetDate).format('YYYY-MM-DD');
  }

  /**
   * Gets the start date (Sunday) of Week 1 for a given year.
   * Mirrors the logic in WeekCalculationService.
   */
  private getWeek1Start(year: number): Date {
    const firstSaturdayOfJanuary = this.getFirstSaturdayOfJanuary(year);
    const prevYearHasWeek53 = this.weekCalculationService.getMaxWeeksInYear(year - 1) === 53;

    if (prevYearHasWeek53) {
      // If previous year had Week 53, Week 1 starts the day AFTER first Saturday
      const week1Start = new Date(firstSaturdayOfJanuary);
      week1Start.setDate(week1Start.getDate() + 1);
      return week1Start;
    } else {
      // Normal case: Week 1 starts 6 days before first Saturday
      const week1Start = new Date(firstSaturdayOfJanuary);
      week1Start.setDate(week1Start.getDate() - 6);
      return week1Start;
    }
  }

  /**
   * Gets the first Saturday of January for a given year
   */
  private getFirstSaturdayOfJanuary(year: number): Date {
    const firstDayOfYear = new Date(year, 0, 1);
    const dayOfWeek = firstDayOfYear.getDay();
    const daysToFirstSaturday = (6 - dayOfWeek + 7) % 7;
    return new Date(year, 0, 1 + daysToFirstSaturday);
  }

  /**
   * Generate week data for dropdown, spanning from late previous year
   * through all weeks of current year to handle year boundaries
   */
  private generateWeekData(): void {
    const currentYear = new Date().getFullYear();
    const prevYear = currentYear - 1;
    const weeks: { key: string; value: string }[] = [];

    // Add weeks from previous year (last few weeks that might include Week 53)
    const prevYearMaxWeeks = this.weekCalculationService.getMaxWeeksInYear(prevYear);
    const startWeek = Math.max(48, prevYearMaxWeeks - 5);
    for (let i = startWeek; i <= prevYearMaxWeeks; i++) {
      const sundayDate = this.getSundayOfWeek(prevYear, i);
      weeks.push({
        key: sundayDate,
        value: `Week ${i.toString().padStart(2, '0')}, ${prevYear}`
      });
    }

    // Add all weeks for current year
    const currentYearMaxWeeks = this.weekCalculationService.getMaxWeeksInYear(currentYear);
    for (let i = 1; i <= currentYearMaxWeeks; i++) {
      const sundayDate = this.getSundayOfWeek(currentYear, i);
      weeks.push({
        key: sundayDate,
        value: `Week ${i.toString().padStart(2, '0')}, ${currentYear}`
      });
    }

    this.CalendarWeekViewForYearData = weeks;
  }

  /**
   * Ensure the selected date exists in dropdown options.
   * If not found, add it dynamically.
   */
  private ensureSelectedDateInOptions(): void {
    if (!this.selectedDate) return;

    // Normalize to Sunday
    const sundayDate = this.normalizeToSunday(this.selectedDate);
    this.selectedDate = sundayDate;

    const exists = this.CalendarWeekViewForYearData.some(item => item.key === sundayDate);
    if (!exists) {
      // Calculate week info for the selected date using WeekCalculationService
      const weekInfo = this.weekCalculationService.calculateWeekInfo(sundayDate);

      const newOption = {
        key: sundayDate,
        value: `Week ${weekInfo.weekNumber.toString().padStart(2, '0')}, ${weekInfo.year}`
      };

      // Insert in correct position (sorted by date)
      const insertIndex = this.CalendarWeekViewForYearData.findIndex(item => item.key > sundayDate);
      if (insertIndex === -1) {
        this.CalendarWeekViewForYearData.push(newOption);
      } else {
        this.CalendarWeekViewForYearData.splice(insertIndex, 0, newOption);
      }
    }
  }

  onDateChange(event: any): void {
    const selectedDate = event.value;
    this.selectedDate = selectedDate;

    // Find the display value for the selected date
    const selectedOption = this.CalendarWeekViewForYearData.find(item => item.key === selectedDate);
    const weekDisplay = selectedOption ? selectedOption.value : '';

    this.selectionChange.emit({
      date: selectedDate,
      weekDisplay: weekDisplay
    });
  }
}
